import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, X, Search, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";

const ROLE_LABEL = { company_admin: "Admin de Empresa", editor: "Edición", viewer: "Visualización" };
const ROLE_BADGE = {
  company_admin: "bg-slate-900 text-white",
  editor: "bg-blue-50 text-blue-600",
  viewer: "bg-slate-100 text-slate-600",
};
const MODULES = [
  ["dashboard", "Estado de Resultados"],
  ["costo_proveedores", "Costo Proveedores"],
  ["precios", "Precios Mont Belvieu"],
  ["aduanas", "Agencias Aduanales"],
  ["inspectores", "Inspectores / Facturas"],
  ["presupuesto", "Presupuesto"],
  ["compras", "Compras"],
  ["calendario", "Calendario"],
];
const defaultLevel = (role) => (role === "editor" ? "edit" : role === "viewer" ? "view" : "none");

export default function Usuarios() {
  const { isSuperadmin } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [assignable, setAssignable] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [assignments, setAssignments] = useState([]);

  const load = useCallback(() => {
    api
      .get(`/users?search=${encodeURIComponent(search)}&sort=${sort}&order=${order}&page=${page}`)
      .then((r) => setData(r.data));
  }, [search, sort, order, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/companies/mine").then((r) => {
      const list = isSuperadmin ? r.data : r.data.filter((c) => c.role === "company_admin");
      setAssignable(list);
    });
  }, [isSuperadmin]);

  const totalPages = Math.max(1, Math.ceil(data.total / 10));

  const toggleSort = (col) => {
    if (sort === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder("asc"); }
    setPage(1);
  };

  const openNew = () => {
    setEdit(null);
    setForm({ name: "", email: "", password: "" });
    setAssignments(assignable[0] ? [{ company_id: assignable[0].id, role: "viewer", permissions: {} }] : []);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEdit(u);
    setForm({ name: u.name || "", email: u.email, password: "" });
    setAssignments((u.assignments || []).map((a) => ({ company_id: a.company_id, role: a.role, permissions: a.permissions || {} })));
    setOpen(true);
  };

  const addAssignment = () => {
    const used = new Set(assignments.map((a) => a.company_id));
    const next = assignable.find((c) => !used.has(c.id)) || assignable[0];
    if (next) setAssignments([...assignments, { company_id: next.id, role: "viewer", permissions: {} }]);
  };
  const updateAssignment = (i, key, val) => {
    const copy = [...assignments];
    copy[i] = { ...copy[i], [key]: val };
    if (key === "role") copy[i].permissions = {}; // reset al cambiar de rol
    setAssignments(copy);
  };
  const RANK = { none: 0, view: 1, edit: 2, full: 3 };
  const levelFromFlags = (ver, editar, eliminar) =>
    eliminar ? "full" : editar ? "edit" : ver ? "view" : "none";
  const togglePerm = (i, mod, which) => {
    const cur = assignments[i].permissions?.[mod] ?? defaultLevel(assignments[i].role);
    let ver = RANK[cur] >= 1, editar = RANK[cur] >= 2, eliminar = RANK[cur] >= 3;
    if (which === "ver") { ver = !ver; if (!ver) { editar = false; eliminar = false; } }
    if (which === "editar") { editar = !editar; if (editar) ver = true; else eliminar = false; }
    if (which === "eliminar") { eliminar = !eliminar; if (eliminar) { editar = true; ver = true; } }
    const copy = [...assignments];
    copy[i] = { ...copy[i], permissions: { ...(copy[i].permissions || {}), [mod]: levelFromFlags(ver, editar, eliminar) } };
    setAssignments(copy);
  };
  const removeAssignment = (i) => setAssignments(assignments.filter((_, idx) => idx !== i));

  const normalizeAssignments = () => assignments.map((a) => {
    if (a.role === "company_admin") return { company_id: a.company_id, role: a.role, permissions: {} };
    const perms = {};
    MODULES.forEach(([key]) => { perms[key] = a.permissions?.[key] ?? defaultLevel(a.role); });
    return { company_id: a.company_id, role: a.role, permissions: perms };
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payloadAssignments = normalizeAssignments();
      if (edit) {
        const payload = { name: form.name, assignments: payloadAssignments };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${edit.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { ...form, assignments: payloadAssignments });
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";
  const Th = ({ col, children }) => (
    <th className="px-4 py-3 font-medium text-left">
      <button onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`user-sort-${col}`}>
        {children} <ArrowUpDown className="h-3 w-3" />
      </button>
    </th>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="user-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre o correo…"
            className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
        <button data-testid="user-new" onClick={openNew} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Nuevo usuario</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              <Th col="name">Nombre</Th>
              <Th col="email">Correo</Th>
              <th className="px-4 py-3 font-medium text-left">Empresas y Rol</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {data.items.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">Sin usuarios</td></tr>}
            {data.items.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-medium text-slate-900">{u.name} {u.is_superadmin && <span className="ml-1 inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-600">Super Admin</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {u.is_superadmin && <span className="text-xs text-slate-400">Acceso global</span>}
                    {(u.assignments || []).map((a) => (
                      <span key={a.company_id} className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${ROLE_BADGE[a.role] || "bg-slate-100"}`} title={ROLE_LABEL[a.role]}>
                        {a.company_nombre}: {ROLE_LABEL[a.role]}
                      </span>
                    ))}
                    {!u.is_superadmin && (u.assignments || []).length === 0 && <span className="text-xs text-slate-400">Sin empresas</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!u.is_superadmin && (
                    <>
                      <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`user-del-${u.id}`} onClick={() => del(u.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>{data.total} usuario(s)</span>
          <div className="flex items-center gap-2">
            <button data-testid="user-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <span>Página {page} de {totalPages}</span>
            <button data-testid="user-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-heading font-semibold text-slate-900">{edit ? "Editar usuario" : "Nuevo usuario"}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-3">
              <div>
                <label className="text-xs text-slate-500">Nombre</label>
                <input data-testid="user-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs text-slate-500">Correo</label>
                <input data-testid="user-email" type="email" className={input} value={form.email} disabled={!!edit} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs text-slate-500">{edit ? "Nueva contraseña (opcional)" : "Contraseña"}</label>
                <input data-testid="user-password" type="password" className={input} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!edit} />
              </div>

              <div className="border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Empresas asignadas</label>
                  <button type="button" data-testid="user-add-assignment" onClick={addAssignment} className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Agregar</button>
                </div>
                <div className="space-y-2" data-testid="assignments-list">
                  {assignments.length === 0 && <p className="text-xs text-slate-400">Sin empresas asignadas. Agrega al menos una para dar acceso.</p>}
                  {assignments.map((a, i) => (
                    <div key={a.company_id || i} className="rounded-md border border-slate-200 p-2.5 space-y-2" data-testid={`assignment-${i}`}>
                      <div className="flex items-center gap-2">
                        <select data-testid={`assignment-company-${i}`} className={input} value={a.company_id} onChange={(e) => updateAssignment(i, "company_id", e.target.value)}>
                          {assignable.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                        <select data-testid={`assignment-role-${i}`} className={`${input} max-w-[170px]`} value={a.role} onChange={(e) => updateAssignment(i, "role", e.target.value)}>
                          <option value="viewer">Visualización</option>
                          <option value="editor">Edición</option>
                          <option value="company_admin">Admin de Empresa</option>
                        </select>
                        <button type="button" onClick={() => removeAssignment(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      {a.role === "company_admin" ? (
                        <p className="text-[11px] text-slate-400">Acceso total a todos los módulos de la empresa.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid={`assignment-perms-${i}`}>
                          <div className="flex items-center justify-end gap-3 pr-1 text-[10px] uppercase tracking-wider text-slate-400">
                            <span className="w-12 text-center">Ver</span>
                            <span className="w-12 text-center">Editar</span>
                            <span className="w-14 text-center">Eliminar</span>
                          </div>
                          {MODULES.map(([key, label]) => {
                            const cur = a.permissions?.[key] ?? defaultLevel(a.role);
                            const ver = RANK[cur] >= 1, editar = RANK[cur] >= 2, eliminar = RANK[cur] >= 3;
                            return (
                              <div key={key} className="flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-600 truncate flex-1">{label}</span>
                                <input type="checkbox" data-testid={`perm-${i}-${key}-ver`} checked={ver} onChange={() => togglePerm(i, key, "ver")} className="w-12 rounded border-slate-300" />
                                <input type="checkbox" data-testid={`perm-${i}-${key}-editar`} checked={editar} onChange={() => togglePerm(i, key, "editar")} className="w-12 rounded border-slate-300" />
                                <input type="checkbox" data-testid={`perm-${i}-${key}-eliminar`} checked={eliminar} onChange={() => togglePerm(i, key, "eliminar")} className="w-14 rounded border-slate-300" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                <button data-testid="user-save" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2"><Plus className="h-4 w-4" /> Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
